import React from 'react';
import styled from 'styled-components';
import {Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';

const tokens = tokensForThemeProvider.sky.sys;
const compTokens = tokensForThemeProvider.sky.comp;

/** Same informational-alert shell as `AIBanner` (12px left accent bar, `comp.alerts.color...`
 * tokens, asymmetric padding) — no button here, so it is a single-line wrapper rather than the
 * `space-between` two-column layout `AIBanner` uses. */
const BannerWrapper = styled.div`
	display: flex;
	align-items: center;
	padding: ${tokens.spacing['200']} ${tokens.spacing['400']} ${tokens.spacing['200']} ${tokens.spacing['600']};
	background: ${compTokens.alerts.color.background.information};
	border-left: 12px solid ${compTokens.alerts.color.bar.information};
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(35, 35, 52, 0.12);
`;

/** Tells the editor whether readers are actually isolated from whatever is currently on screen. When
 * `reloadRequired` says what is on screen no longer matches what is stored, it instead says it cannot
 * speak for readers at all and that the page has to be reloaded. Recovery is deliberately a page reload
 * and not a control in here: only a fresh load is guaranteed to show what is stored, and this banner
 * stays text-only.
 * Reader isolation is only real while a draft is genuinely open (`draftState === 'DRAFT'`) — any
 * other value, including one the client did not recognise (the context's conservative fallback),
 * must not claim isolation it cannot back up. `'UNPUBLISHED'` and `'PUBLISHED'` both mean no draft
 * is open, but they need different copy: an unpublished report has never had a published version
 * for readers to see, while a published one already does — `'PUBLISHED'` is reachable in edit mode
 * when `beginEdit()` fails to open a draft on a report that was already published, and must not
 * claim the report "has never been published". In view mode there is nothing to isolate readers
 * from, so the banner instead states when the report was last published, and renders nothing for
 * a report that has never been published (no `publishedAt` to show). Copy is resolved to plain
 * strings via `useIntl()` — this component family's DS `Text` treats children as text content and
 * drops nested elements, so rich content (including the publish date) must be resolved before being
 * passed in rather than nested as `FormattedMessage`/`FormattedDate`. `formatDate` still defers the
 * actual format to the locale. */
export function PublishStateBanner(): JSX.Element | null {
	const {mode, draftState, publishedAt, reloadRequired} = useReportBuilder();
	const {formatMessage, formatDate} = useIntl();
	const infoColor = compTokens.alerts.color.text.information;

	// Takes precedence over every state below, in either mode. Once what is on screen is out of step with
	// what is stored, no statement about readers holds: the published-version promise would be describing a
	// draft that has been dropped, the live-changes copy would be describing changes that were never saved,
	// and the publish date would be dating a version that may not be the one stored. So the banner states
	// the divergence and asks for a page reload instead of inviting a publish the bar has disabled anyway.
	if (reloadRequired) {
		return (
			<BannerWrapper data-cy={'ai-report-publish-state-banner'}>
				<Text color={infoColor}>{formatMessage({id: 'ai_report_builder.definition_out_of_step'})}</Text>
			</BannerWrapper>
		);
	}

	if (mode === 'view') {
		if (publishedAt === null) {
			return null;
		}
		return (
			<BannerWrapper data-cy={'ai-report-publish-state-banner'}>
				<Text color={infoColor}>
					{formatMessage({id: 'ai_report_builder.published_on'}, {date: formatDate(publishedAt)})}
				</Text>
			</BannerWrapper>
		);
	}

	const messageId =
		draftState === 'DRAFT'
			? 'ai_report_builder.readers_see_published'
			: draftState === 'UNPUBLISHED'
			? 'ai_report_builder.readers_see_live'
			: 'ai_report_builder.readers_see_live_no_draft';

	return (
		<BannerWrapper data-cy={'ai-report-publish-state-banner'}>
			<Text color={infoColor}>{formatMessage({id: messageId})}</Text>
		</BannerWrapper>
	);
}
